// src/pages/Extensions.tsx
//
// The /extensions route.
//
// Same structure as the /projects page, deliberately: a page-level <main> on the
// shared 90%/1120px measure, the 4.5rem -> 6rem section padding, a mono eyebrow,
// the h1, a lede, then a feature component that owns the cards. The header
// styles are the ones already in use on Project.tsx and the About page rather
// than a new set.
//
// Visual reference is the Projects page. Every token here — the page measure,
// the eyebrow and heading scale, the lede measure, the card treatment, the
// reveal — is copied from existing pages so the route reads as part of the same
// site rather than a page bolted onto it.
//
// No closing CTA section, for the same reason the projects page has none: the
// Footer's own call to action already opens the shared Query window, and a
// second one here would be another link to the same place.
import styled from 'styled-components';
import { motion, useReducedMotion } from 'framer-motion';

import { STAGGER, motionSet } from '../components/about/motion';
import ExtensionGrid from '../components/extensions/ExtensionGrid';
import Footer from '../components/Footer';
import { minDevice } from '../styles/breakpoints';

const monoStack = "ui-monospace, SFMono-Regular, 'SF Mono', Consolas, 'Liberation Mono', Menlo, monospace";

const Page = styled.main`
  width: 90%;
  max-width: 1120px;
  margin: 0 auto;
  /* The Navbar is sticky rather than fixed, so the page flows under it and needs
     no offset. Matches the 4.5rem -> 6rem rhythm of the Home sections. */
  padding: 4.5rem 0;

  @media ${minDevice.laptop} {
    padding: 6rem 0;
  }
`;

const Header = styled(motion.header)`
  margin-bottom: 2.75rem;
`;

const Eyebrow = styled(motion.p)`
  margin: 0 0 1rem;
  font-family: ${monoStack};
  font-size: 0.75rem;
  font-weight: 500;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.accent};
`;

/** h1: this is the page's own heading, not a section heading under another. */
const Title = styled(motion.h1)`
  margin: 0;
  font-size: 1.6rem;
  line-height: 1.25;
  letter-spacing: -0.01em;
  font-weight: 700;
  color: ${({ theme }) => theme.headingColor};

  @media ${minDevice.tablet} {
    font-size: 2rem;
  }
`;

const Lede = styled(motion.p)`
  margin: 1.25rem 0 0;
  max-width: 58ch;
  font-size: 1rem;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.secondary};
`;

const headingId = 'extensions-heading';
const viewport = { once: true, amount: 0.3 } as const;

const Extensions = () => {
  const reduceMotion = useReducedMotion() ?? false;
  const anim = motionSet(reduceMotion);

  return (
    <>
      <Page>
        <Header
          variants={anim.group(STAGGER.group)}
          initial="hidden"
          whileInView="visible"
          viewport={viewport}
        >
          <Eyebrow variants={anim.reveal}>Developer tools</Eyebrow>
          <Title id={headingId} variants={anim.reveal}>
            Extensions
          </Title>
          <Lede variants={anim.reveal}>
            Editor extensions I have built and published to solve practical problems in my own
            development workflow, and to make the same steps easier for other developers.
          </Lede>
        </Header>

        <ExtensionGrid />
      </Page>

      <Footer />
    </>
  );
};

export default Extensions;
